import { useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { confirmPasswordReset } from '../api/auth'
import { ApiError } from '../api/client'
import { AuthScreenLayout } from '../components/auth/AuthScreenLayout'

export const ResetPasswordScreen = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = useMemo(() => searchParams.get('token')?.trim() ?? '', [searchParams])
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setMessage(null)
    if (!token) {
      setError('Ссылка для сброса пароля недействительна или устарела')
      return
    }
    if (password.length < 8) {
      setError('Пароль должен быть не короче 8 символов')
      return
    }
    if (password !== confirm) {
      setError('Пароли не совпадают')
      return
    }
    setLoading(true)
    try {
      const response = await confirmPasswordReset(token, password)
      setMessage(response.message || 'Пароль обновлён. Теперь можно войти.')
      window.setTimeout(() => navigate('/login', { replace: true }), 1200)
    } catch (resetError) {
      if (resetError instanceof ApiError) {
        setError(resetError.message || 'Не удалось обновить пароль')
      } else {
        setError(resetError instanceof Error ? resetError.message : 'Не удалось обновить пароль')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthScreenLayout>
      <div className="market-auth__card">
        <header className="market-auth__card-header">
          <h2 className="market-auth__card-title">Новый пароль</h2>
          <p className="market-auth__card-lead">Задайте пароль для входа в Провизиум</p>
        </header>

        {!token ? (
          <p className="echat-error market-auth__error" role="alert">
            Ссылка для сброса пароля недействительна или устарела. Запросите новую на странице восстановления.
          </p>
        ) : (
          <form className="market-auth__form" onSubmit={(event) => void handleSubmit(event)}>
            <label className="market-auth-field">
              <span className="market-auth-field__label">
                Новый пароль <span className="market-auth-field__required">*</span>
              </span>
              <span className="market-auth-field__control">
                <input
                  className="market-auth-field__input"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </span>
            </label>

            <label className="market-auth-field">
              <span className="market-auth-field__label">
                Повторите пароль <span className="market-auth-field__required">*</span>
              </span>
              <span className="market-auth-field__control">
                <input
                  className="market-auth-field__input"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                />
              </span>
            </label>

            {message ? <p className="market-auth__message">{message}</p> : null}
            {error ? (
              <p className="echat-error market-auth__error" role="alert">
                {error}
              </p>
            ) : null}

            <button type="submit" className="echat-btn echat-btn--primary market-auth__submit" disabled={loading}>
              {loading ? 'Сохранение…' : 'Сохранить пароль'}
            </button>
          </form>
        )}

        <div className="market-auth__footer">
          <Link className="market-auth__back-link" to="/forgot-password">
            Запросить новую ссылку
          </Link>
          <Link className="market-auth__back-link" to="/login">
            ← Вернуться ко входу
          </Link>
        </div>
      </div>
    </AuthScreenLayout>
  )
}
